import { useMemo, useReducer } from 'react';
import { TourStep, TourAction } from '@context/tour-context';

interface UseTourReturnValue {
  tourState: 'off' | 'show' | 'finish';
  tourSteps: TourStep[];
  dispatch: (action: TourAction) => void;
}

interface TourState {
  tourState: 'off' | 'show' | 'finish';
  tourSteps: TourStep[];
}

function tourReducer(state: TourState, action: TourAction): TourState {
  switch (action.type) {
    case 'INITIALIZE_TOUR':
      return { ...state, tourSteps: validateSteps(action.value), tourState: 'show' };
    case 'START':
      return { ...state, tourState: 'show' };
    case 'FINISH':
      return { ...state, tourState: 'finish' };
    case 'COMPLETE_CURRENT_STEP':
      return {
        ...state,
        tourSteps: completeCurrentStep(state.tourSteps),
        tourState: isLastStep(state.tourSteps) ? 'finish' : state.tourState,
      };
    case 'GO_TO_NEXT_STEP':
      return {
        ...state,
        tourSteps: completeCurrentStep(state.tourSteps, true),
        tourState: isLastStep(state.tourSteps) ? 'finish' : state.tourState,
      };
    case 'GO_TO_PREV_STEP':
      return { ...state, tourSteps: moveToPreviousStep(state.tourSteps) };
    case 'MAKE_STEP_ACTIVE':
      return {
        ...state,
        tourSteps: activateStep(state.tourSteps, action.value, action.completePreviousSteps),
      };
  }
}

function isLastStep(tourSteps: TourStep[]) {
  return tourSteps[tourSteps.length - 1]?.active === true;
}

/**
 * Activate the step corresponding to the provided ID. Only steps that proceed completed steps
 * can be activated. We require an ID instead of simply activating whatever step comes after the last
 * completed step because we want to prevent activating the wrong step if the user veers off the golden
 * path for the tour.
 * @param tourSteps The loaded tour steps
 * @param id The ID of the step to be activated
 */
function activateStep(tourSteps: TourStep[], id: string, completePreviousSteps = false): TourStep[] {
  const stepIndex = tourSteps.findIndex((step) => step.id === id);
  if (stepIndex === -1) return tourSteps;

  const prevStepIndex = stepIndex - 1;
  const nextStepIndex = stepIndex + 1;
  const isPrevStepCompleted = prevStepIndex >= 0 && tourSteps[prevStepIndex].completed;
  const isNextStepCompleted = nextStepIndex < tourSteps.length && tourSteps[nextStepIndex].completed;

  // Only activate the step if it is the first step OR the prev step is completed OR we have
  // specified that all previous steps should be completed AND we weren't already further ahead
  // in the tour than this step (i.e. none of the next steps had been completed)
  if (stepIndex === 0 || isPrevStepCompleted) {
    return tourSteps.map((step, i) => {
      if (i === stepIndex) {
        return { ...step, active: true, completed: false };
      } else return { ...step };
    });
  } else if (completePreviousSteps && (nextStepIndex === tourSteps.length || !isNextStepCompleted)) {
    return tourSteps.map((step, i) => {
      if (i < stepIndex) {
        return { ...step, active: false, completed: true };
      } else if (i === stepIndex) {
        return { ...step, active: true, completed: false };
      } else {
        return { ...step, active: false, completed: false };
      }
    });
  } else return tourSteps;
}

function moveToPreviousStep(tourSteps: TourStep[]): TourStep[] {
  let currentStepIndex: number | undefined;

  return [...tourSteps]
    .reverse()
    .map((step, i) => {
      if (step.active || step.completed) {
        currentStepIndex = i;
        return { ...step, active: false, completed: false };
      } else if (currentStepIndex && i === currentStepIndex + i) {
        return { ...step, active: true, completed: false };
      } else return step;
    })
    .reverse();
}

function completeCurrentStep(tourSteps: TourStep[], advanceToNextStep = false): TourStep[] {
  let activeStepIndex: number | undefined;

  return tourSteps.map((step, i) => {
    if (step.active) {
      activeStepIndex = i;
      return { ...step, active: false, completed: true };
    } else if (advanceToNextStep && activeStepIndex !== undefined && i === activeStepIndex + 1) {
      return { ...step, active: true };
    } else return { ...step, active: false };
  });
}

function validateSteps(steps: TourStep[], errorMessage?: string): TourStep[] {
  if (!areStepsValid(steps)) {
    throw new Error(errorMessage ?? 'Steps provided to tour are not valid.');
  }

  return steps;
}

function areStepsValid(steps: TourStep[]): boolean {
  let numActiveSteps = 0;

  for (const step of steps) {
    if (!step.content || !step.id || !['tooltip', 'dialog'].includes(step.type)) return false;
    if (step.active) {
      numActiveSteps++;
      if (numActiveSteps > 1) return false;
    }
  }

  return true;
}

function isStepActive(steps: TourStep[], id: string): boolean {
  return !!steps.find((step) => step.id === id)?.active;
}

function isStepCompleted(steps: TourStep[], id: string): boolean {
  return !!steps.find((step) => step.id === id)?.completed;
}

/**
 * Returns the props needed for the tour context
 */
function useTour(): UseTourReturnValue {
  const [tourState, dispatch] = useReducer(tourReducer, { tourState: 'off', tourSteps: [] });

  const tourProps = useMemo(
    () => ({
      tourState: tourState.tourState,
      tourSteps: tourState.tourSteps,
      dispatch,
    }),
    [tourState, dispatch]
  );

  return tourProps;
}

export default useTour;
export {
  moveToPreviousStep,
  completeCurrentStep,
  activateStep,
  validateSteps,
  areStepsValid,
  isStepActive,
  isStepCompleted,
};
